/**
 * Pure line-structure helpers of the Lines panel (W2-07 lines-panel; DESIGN.md §4.10, §5.5, rpe-format F§9.5,
 * F§10.4). No React, no services: everything here is a function of the document (and, for the "now" filters, of a
 * NowState computed from the engine), so the panel, the commands, the switcher and the palette provider agree.
 *
 *  - Row model: one `LineInfo` per line (export index, derived type, notes count, depth, group, children).
 *  - Views: the tree (parent/child, children in export order, collapsible) and the export order (flat).
 *  - Structure rules: cycle-proof parent checks with the reason, export-order moves, previous/next line.
 *  - Search: `#12` (export index) and fuzzy names, shared by the panel search, the switcher and the `#` palette.
 *  - Filters: Visible now, Animating now, Has notes, by group, by type (AND-ed), plus the text search.
 *  - Preview filter summary: hide / solo / layer mute / dim change the preview ("preview differs from export");
 *    lock only blocks editing and never counts.
 */

import type { LineTypeChoice } from '../../core/commands/types';
import { isGifAsset, kindOfLine } from '../../core/model/lineKind';
import type {
  AssetId,
  AssetRef,
  ChartDoc,
  DocIndexes,
  GroupId,
  Line,
  LineId,
  LineKind,
  TrackId,
  UiElement,
} from '../../core/model/types';
import type { ViewFilterState } from '../../state/types';

// ---------------------------------------------------------------------------------------------------------------
// Types and line info
// ---------------------------------------------------------------------------------------------------------------

/** What the Type column shows and the type filter matches (Helper = an invisible normal line without notes). */
export type LineTypeId = 'line' | 'helper' | 'text' | 'image' | 'gif' | 'paint' | 'uiMover';

export const LINE_TYPE_IDS: readonly LineTypeId[] = ['line', 'helper', 'text', 'image', 'gif', 'paint', 'uiMover'];

export const UI_ELEMENT_IDS: readonly UiElement[] = ['pause', 'combonumber', 'combo', 'score', 'bar', 'name', 'level'];

/** lucide icon per type (UI movers use their element's icon). */
export const TYPE_ICONS: Readonly<Record<LineTypeId, string>> = {
  line: 'Minus',
  helper: 'Anchor',
  text: 'Type',
  image: 'Image',
  gif: 'Film',
  paint: 'Brush',
  uiMover: 'Gauge',
};

export const UI_ELEMENT_ICONS: Readonly<Record<UiElement, string>> = {
  pause: 'Pause',
  combonumber: 'Hash',
  combo: 'Award',
  score: 'Trophy',
  bar: 'RectangleHorizontal',
  name: 'Music',
  level: 'Signal',
};

export interface LineInfo {
  readonly id: LineId;
  readonly line: Line;
  /** Export index (position in lineOrder). */
  readonly index: number;
  readonly name: string;
  readonly parent: LineId | null;
  /** Depth in the parent tree (0 = no parent). */
  readonly depth: number;
  readonly kind: LineKind;
  readonly type: LineTypeId;
  readonly uiElement: UiElement | null;
  readonly notes: number;
  readonly zOrder: number;
  readonly group: GroupId | null;
  readonly groupName: string | null;
  readonly groupColor: string | null;
  readonly tag: string | null;
  readonly rotateWithParent: boolean;
  /** Direct children, in export order. */
  readonly children: readonly LineId[];
}

/** All line infos keyed by id, plus the export order. */
export interface LineTable {
  readonly order: readonly LineId[];
  readonly byId: ReadonlyMap<LineId, LineInfo>;
}

/** Phira's derived kind (F§9.5), computed from the record and its tracks (core `kindOfLine`). */
export function kindOf(doc: Pick<ChartDoc, 'assets'>, idx: Pick<DocIndexes, 'tracksOfLine'>, line: Line): LineKind {
  return kindOfLine(doc, idx, line);
}

/** Keys of a line's tracks of one property, over all layers. */
function keyCountOf(
  idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack'>,
  line: LineId,
  prop: 'text' | 'paint' | 'gif',
): number {
  let n = 0;
  for (const t of idx.tracksOfLine(line).get(prop) ?? []) n += idx.keysByTrack(t).length;
  return n;
}

/** True when no alpha key of the line is above 0 (the line never shows; absent opacity hides it too, F§6.2). */
export function neverVisible(
  doc: Pick<ChartDoc, 'keys'>,
  idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack'>,
  line: LineId,
): boolean {
  const tracks: readonly TrackId[] = idx.tracksOfLine(line).get('alpha') ?? [];
  for (const t of tracks) {
    for (const k of idx.keysByTrack(t)) {
      const key = doc.keys.get(k);
      if (key === undefined) continue;
      if ((typeof key.vIn === 'number' && key.vIn > 0) || (typeof key.vOut === 'number' && key.vOut > 0)) return false;
    }
  }
  return true;
}

/** The type the panel shows for a line. */
export function typeOf(
  doc: Pick<ChartDoc, 'assets' | 'keys'>,
  idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack' | 'notesByLine'>,
  line: Line,
  kind: LineKind = kindOf(doc, idx, line),
): LineTypeId {
  if (line.attachUI !== null) return 'uiMover';
  switch (kind) {
    case 'text':
      return 'text';
    case 'texture':
      return 'image';
    case 'gif':
      return 'gif';
    case 'paint':
      return 'paint';
    case 'normal':
      return idx.notesByLine(line.id).length === 0 && neverVisible(doc, idx, line.id) ? 'helper' : 'line';
  }
}

/** Depth of a line in the parent tree (cycles, which the model forbids, are cut). */
function depthOf(doc: Pick<ChartDoc, 'lines'>, id: LineId): number {
  let depth = 0;
  const seen = new Set<LineId>([id]);
  let cur = doc.lines.get(id)?.parent ?? null;
  while (cur !== null && !seen.has(cur)) {
    seen.add(cur);
    depth++;
    cur = doc.lines.get(cur)?.parent ?? null;
  }
  return depth;
}

/** Builds the row data of every line (O(lines + tracks of alpha)). */
export function buildLineTable(doc: ChartDoc, idx: DocIndexes): LineTable {
  const byId = new Map<LineId, LineInfo>();
  const order = doc.lineOrder;
  const children = new Map<LineId, LineId[]>();
  for (const id of order) {
    const p = doc.lines.get(id)?.parent ?? null;
    if (p === null) continue;
    const list = children.get(p);
    if (list === undefined) children.set(p, [id]);
    else list.push(id);
  }
  order.forEach((id, index) => {
    const line = doc.lines.get(id);
    if (line === undefined) return;
    const kind = kindOf(doc, idx, line);
    const group = line.group === null ? undefined : doc.groups.get(line.group);
    byId.set(id, {
      id,
      line,
      index,
      name: line.name,
      parent: line.parent,
      depth: depthOf(doc, id),
      kind,
      type: typeOf(doc, idx, line, kind),
      uiElement: line.attachUI,
      notes: idx.notesByLine(id).length,
      zOrder: line.zOrder,
      group: group === undefined ? null : group.id,
      groupName: group === undefined ? null : group.name,
      groupColor: group?.color ?? null,
      tag: line.editor.tag,
      rotateWithParent: line.rotateWithParent,
      children: children.get(id) ?? [],
    });
  });
  return { order, byId };
}

// ---------------------------------------------------------------------------------------------------------------
// Tree structure and parents
// ---------------------------------------------------------------------------------------------------------------

/** Ancestors of a line, nearest first. */
export function ancestorsOf(doc: Pick<ChartDoc, 'lines'>, id: LineId): LineId[] {
  const out: LineId[] = [];
  const seen = new Set<LineId>([id]);
  let cur = doc.lines.get(id)?.parent ?? null;
  while (cur !== null && !seen.has(cur)) {
    out.push(cur);
    seen.add(cur);
    cur = doc.lines.get(cur)?.parent ?? null;
  }
  return out;
}

/** True when `ancestor` is `line` itself or one of its ancestors. */
export function isAncestorOrSelf(doc: Pick<ChartDoc, 'lines'>, ancestor: LineId, line: LineId): boolean {
  return ancestor === line || ancestorsOf(doc, line).includes(ancestor);
}

/** Every descendant of a line (children first, breadth-first). */
export function descendantsOf(doc: Pick<ChartDoc, 'lines' | 'lineOrder'>, id: LineId): LineId[] {
  const kids = new Map<LineId, LineId[]>();
  for (const l of doc.lineOrder) {
    const p = doc.lines.get(l)?.parent ?? null;
    if (p === null) continue;
    const list = kids.get(p);
    if (list === undefined) kids.set(p, [l]);
    else list.push(l);
  }
  const out: LineId[] = [];
  const queue = [...(kids.get(id) ?? [])];
  const seen = new Set<LineId>([id]);
  for (let next = queue.shift(); next !== undefined; next = queue.shift()) {
    if (seen.has(next)) continue;
    seen.add(next);
    out.push(next);
    queue.push(...(kids.get(next) ?? []));
  }
  return out;
}

/** Why `lines` cannot all be attached to `parent` (null = allowed). */
export type ReparentProblem =
  | { readonly kind: 'cycle'; readonly line: LineId; readonly parent: LineId }
  | { readonly kind: 'missing'; readonly line: LineId }
  | { readonly kind: 'unchanged' };

export function reparentProblem(
  doc: Pick<ChartDoc, 'lines'>,
  lines: readonly LineId[],
  parent: LineId | null,
): ReparentProblem | null {
  if (parent !== null && !doc.lines.has(parent)) return { kind: 'missing', line: parent };
  let changes = 0;
  for (const id of lines) {
    const rec = doc.lines.get(id);
    if (rec === undefined) return { kind: 'missing', line: id };
    if (parent !== null && isAncestorOrSelf(doc, id, parent)) return { kind: 'cycle', line: id, parent };
    if (rec.parent !== parent) changes++;
  }
  return changes === 0 ? { kind: 'unchanged' } : null;
}

/** Lines that `line` may be attached to (not itself or a descendant), in export order. */
export function parentCandidates(doc: Pick<ChartDoc, 'lines' | 'lineOrder'>, line: LineId): LineId[] {
  const banned = new Set([line, ...descendantsOf(doc, line)]);
  return doc.lineOrder.filter((id) => !banned.has(id));
}

/**
 * Export order with `moving` (kept in their own relative order) placed before or after `target`. Null when nothing
 * would change or `target` is one of the moved lines.
 */
export function moveInOrder(
  order: readonly LineId[],
  moving: readonly LineId[],
  target: LineId,
  where: 'before' | 'after',
): LineId[] | null {
  const set = new Set(moving.filter((id) => order.includes(id)));
  if (set.size === 0 || set.has(target) || !order.includes(target)) return null;
  const moved = order.filter((id) => set.has(id));
  const rest = order.filter((id) => !set.has(id));
  const at = rest.indexOf(target) + (where === 'after' ? 1 : 0);
  const next = [...rest.slice(0, at), ...moved, ...rest.slice(at)];
  return next.every((id, i) => id === order[i]) ? null : next;
}

/** Export order with the moved lines shifted one step up (-1) or down (+1) as a block. Null when at the edge. */
export function shiftInOrder(order: readonly LineId[], moving: readonly LineId[], dir: -1 | 1): LineId[] | null {
  const set = new Set(moving);
  const positions = order.map((id, i) => (set.has(id) ? i : -1)).filter((i) => i >= 0);
  if (positions.length === 0) return null;
  const first = positions[0] ?? 0;
  const last = positions[positions.length - 1] ?? 0;
  if (dir < 0) {
    const target = order[first - 1];
    return target === undefined ? null : moveInOrder(order, moving, target, 'before');
  }
  const target = order[last + 1];
  return target === undefined ? null : moveInOrder(order, moving, target, 'after');
}

/** The line before / after `current` in export order (wraps around; the first line when nothing is active). */
export function neighbourLine(order: readonly LineId[], current: LineId | null, dir: -1 | 1): LineId | null {
  if (order.length === 0) return null;
  const i = current === null ? -1 : order.indexOf(current);
  if (i < 0) return (dir > 0 ? order[0] : order[order.length - 1]) ?? null;
  return order[(i + dir + order.length) % order.length] ?? null;
}

// ---------------------------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------------------------

export interface LineRowModel {
  readonly info: LineInfo;
  /** Indentation level in this view (0 in the export-order view). */
  readonly level: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
  /** Shown only because a descendant matches the filters (tree view): drawn muted. */
  readonly context: boolean;
}

/**
 * Tree rows: roots in export order, each followed by its children (export order), depth-first. Children of
 * collapsed lines are left out. With `matches`, only matching lines and their ancestors are listed (ancestors as
 * context rows), and collapsed ancestors of a match are opened so a match is never hidden.
 */
export function treeRows(
  table: LineTable,
  collapsed: ReadonlySet<LineId>,
  matches: ReadonlySet<LineId> | null,
): LineRowModel[] {
  const visible = new Set<LineId>();
  if (matches !== null) {
    for (const id of matches) {
      let cur: LineId | null = id;
      const seen = new Set<LineId>();
      while (cur !== null && !seen.has(cur)) {
        seen.add(cur);
        visible.add(cur);
        cur = table.byId.get(cur)?.parent ?? null;
      }
    }
  }
  const out: LineRowModel[] = [];
  const emitted = new Set<LineId>();
  const walk = (id: LineId, level: number): void => {
    const info = table.byId.get(id);
    if (info === undefined || emitted.has(id)) return;
    if (matches !== null && !visible.has(id)) return;
    emitted.add(id);
    const kids = matches === null ? info.children : info.children.filter((c) => visible.has(c));
    const forcedOpen = matches !== null && kids.length > 0;
    const expanded = forcedOpen || !collapsed.has(id);
    out.push({
      info,
      level,
      hasChildren: info.children.length > 0,
      expanded: info.children.length > 0 && expanded,
      context: matches !== null && !matches.has(id),
    });
    if (expanded) for (const c of kids) walk(c, level + 1);
  };
  for (const id of table.order) {
    const info = table.byId.get(id);
    if (info === undefined) continue;
    // Roots, and lines whose parent is missing (never in a valid document) so nothing is ever lost.
    if (info.parent === null || !table.byId.has(info.parent)) walk(id, 0);
  }
  return out;
}

/** Export-order rows (flat), filtered by `matches` when given. */
export function orderRows(table: LineTable, matches: ReadonlySet<LineId> | null): LineRowModel[] {
  const out: LineRowModel[] = [];
  for (const id of table.order) {
    const info = table.byId.get(id);
    if (info === undefined || (matches !== null && !matches.has(id))) continue;
    out.push({ info, level: 0, hasChildren: info.children.length > 0, expanded: true, context: false });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------------------------

/**
 * Fuzzy score of `query` in `text` (case-insensitive subsequence; null = no match). Higher is better: a prefix,
 * a contiguous run and word starts score more; long gaps and long texts score less.
 */
export function fuzzyScore(query: string, text: string): number | null {
  const q = query.trim().toLowerCase();
  if (q === '') return 0;
  const t = text.toLowerCase();
  const exact = t.indexOf(q);
  if (exact >= 0) {
    const wordStart = exact === 0 || /[\s_\-.:/([]/.test(t[exact - 1] ?? '');
    return 1000 - exact * 2 - (t.length - q.length) + (exact === 0 ? 200 : 0) + (wordStart ? 100 : 0);
  }
  let score = 0;
  let ti = 0;
  let prev = -2;
  for (const ch of q) {
    const found = t.indexOf(ch, ti);
    if (found < 0) return null;
    const wordStart = found === 0 || /[\s_\-.:/([]/.test(t[found - 1] ?? '');
    score += found === prev + 1 ? 15 : 0;
    score += wordStart ? 10 : 0;
    score -= Math.min(10, found - ti);
    prev = found;
    ti = found + 1;
  }
  return score - t.length * 0.1;
}

/** `#12` / `12` / `#` prefix parsing: the export index asked for, or null. */
export function indexQuery(query: string): number | null {
  const m = /^\s*#?\s*(\d+)\s*$/.exec(query);
  if (m === null) return null;
  const n = Number(m[1]);
  return Number.isSafeInteger(n) ? n : null;
}

export interface LineMatch {
  readonly info: LineInfo;
  readonly score: number;
}

/**
 * Lines matching a query, best first: an index query (`#12`, `12`) puts that line first and also lists lines whose
 * index starts with the digits; otherwise names are matched fuzzily. An empty query lists every line in export order.
 */
export function matchLines(table: LineTable, query: string, limit = Infinity): LineMatch[] {
  const q = query.trim().replace(/^#(?=\D)/, '');
  const out: LineMatch[] = [];
  const index = indexQuery(q);
  for (const id of table.order) {
    const info = table.byId.get(id);
    if (info === undefined) continue;
    if (q === '' || q === '#') {
      out.push({ info, score: -info.index });
      continue;
    }
    let score: number | null = null;
    if (index !== null) {
      const digits = String(index);
      const own = String(info.index);
      if (info.index === index) score = 10000;
      else if (own.startsWith(digits)) score = 5000 - own.length;
    }
    const nameScore = fuzzyScore(q.replace(/^#/, ''), info.name);
    if (nameScore !== null) score = Math.max(score ?? -Infinity, nameScore);
    if (score !== null) out.push({ info, score });
  }
  out.sort((a, b) => b.score - a.score || a.info.index - b.info.index);
  return Number.isFinite(limit) ? out.slice(0, limit) : out;
}

// ---------------------------------------------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------------------------------------------

/** "Now" facts at the playhead (computed from the engine, see nowState.ts). */
export interface NowFacts {
  readonly visible: ReadonlySet<LineId>;
  readonly animating: ReadonlySet<LineId>;
}

/** Group filter value for lines without a group (the RPE "Default" group). */
export const NO_GROUP = 'none';
export type GroupFilterValue = GroupId | typeof NO_GROUP;

export interface LineFilterState {
  readonly visibleNow: boolean;
  readonly animatingNow: boolean;
  readonly hasNotes: boolean;
  /** Empty = any group. */
  readonly groups: ReadonlySet<GroupFilterValue>;
  /** Empty = any type. */
  readonly types: ReadonlySet<LineTypeId>;
  readonly text: string;
}

export const NO_FILTERS: LineFilterState = Object.freeze({
  visibleNow: false,
  animatingNow: false,
  hasNotes: false,
  groups: new Set<GroupFilterValue>(),
  types: new Set<LineTypeId>(),
  text: '',
});

export function filtersActive(f: LineFilterState): boolean {
  return f.visibleNow || f.animatingNow || f.hasNotes || f.groups.size > 0 || f.types.size > 0 || f.text.trim() !== '';
}

/** Lines passing every active filter (null = no filter active: every line). */
export function filterLines(table: LineTable, f: LineFilterState, now: NowFacts | null): Set<LineId> | null {
  if (!filtersActive(f)) return null;
  const textHits = f.text.trim() === '' ? null : new Set(matchLines(table, f.text).map((m) => m.info.id));
  const out = new Set<LineId>();
  for (const id of table.order) {
    const info = table.byId.get(id);
    if (info === undefined) continue;
    if (textHits !== null && !textHits.has(id)) continue;
    if (f.hasNotes && info.notes === 0) continue;
    if (f.visibleNow && !(now?.visible.has(id) ?? false)) continue;
    if (f.animatingNow && !(now?.animating.has(id) ?? false)) continue;
    if (f.groups.size > 0 && !f.groups.has(info.group ?? NO_GROUP)) continue;
    if (f.types.size > 0 && !f.types.has(info.type)) continue;
    out.add(id);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Preview-only filters
// ---------------------------------------------------------------------------------------------------------------

export interface PreviewDiffSummary {
  readonly hidden: number;
  readonly solo: number;
  readonly mutedLayers: number;
  readonly dimmed: boolean;
}

/**
 * What makes the preview differ from the export (hidden lines, soloed lines, muted layers, dimmed lines), or null
 * when nothing does. Locked lines and focused hitsounds never count: they do not change what is drawn.
 */
export function previewDifferences(
  f: Pick<ViewFilterState, 'hiddenLines' | 'soloLines' | 'mutedLayers' | 'dimOthers'>,
  exists: (id: LineId) => boolean = () => true,
): PreviewDiffSummary | null {
  let hidden = 0;
  for (const id of f.hiddenLines) if (exists(id)) hidden++;
  let solo = 0;
  for (const id of f.soloLines) if (exists(id)) solo++;
  let mutedLayers = 0;
  for (const [id, layers] of f.mutedLayers) if (exists(id)) mutedLayers += layers.size;
  const dimmed = f.dimOthers > 0;
  return hidden === 0 && solo === 0 && mutedLayers === 0 && !dimmed ? null : { hidden, solo, mutedLayers, dimmed };
}

// ---------------------------------------------------------------------------------------------------------------
// Delete impact
// ---------------------------------------------------------------------------------------------------------------

export interface DeleteImpact {
  /** Lines to delete that exist, in export order. */
  readonly lines: readonly LineId[];
  /** Notes on those lines. */
  readonly notes: number;
  /** Direct children of the deleted lines that are not deleted themselves. */
  readonly children: readonly LineId[];
  /** Descendants that "delete subtree" would delete in addition. */
  readonly subtree: readonly LineId[];
  /** Notes on those descendants. */
  readonly subtreeNotes: number;
}

export function deleteImpact(doc: ChartDoc, idx: DocIndexes, ids: readonly LineId[]): DeleteImpact {
  const set = new Set(ids.filter((id) => doc.lines.has(id)));
  const lines = doc.lineOrder.filter((id) => set.has(id));
  let notes = 0;
  for (const id of lines) notes += idx.notesByLine(id).length;
  const children: LineId[] = [];
  const subtree = new Set<LineId>();
  for (const id of lines) {
    for (const c of idx.childrenOf(id)) if (!set.has(c) && !children.includes(c)) children.push(c);
    for (const d of descendantsOf(doc, id)) if (!set.has(d)) subtree.add(d);
  }
  let subtreeNotes = 0;
  for (const id of subtree) subtreeNotes += idx.notesByLine(id).length;
  const order = (a: LineId, b: LineId): number => idx.lineIndex(a) - idx.lineIndex(b);
  return { lines, notes, children: children.sort(order), subtree: [...subtree].sort(order), subtreeNotes };
}

/** A line label for messages: `#3 Main` style parts. */
export function lineLabelOf(doc: Pick<ChartDoc, 'lines'>, idx: Pick<DocIndexes, 'lineIndex'>, id: LineId): string {
  const line = doc.lines.get(id);
  if (line === undefined) return `#${String(id)}`;
  const name = line.name.trim();
  return name === '' ? `#${String(idx.lineIndex(id))}` : name;
}

// ---------------------------------------------------------------------------------------------------------------
// Line type changes (DESIGN.md §4.21 "Line type", §5.5 "Line types"; core lineType.ts rules)
// ---------------------------------------------------------------------------------------------------------------

/** One thing a type change removes (shown before the change; the change is undoable). */
export type TypeLoss =
  | { readonly kind: 'textTrack'; readonly keys: number }
  | { readonly kind: 'paintTrack'; readonly keys: number }
  /** GIF progress keyframes (Phira refuses GIF progress on anything but a GIF texture). */
  | { readonly kind: 'gifTrack'; readonly keys: number }
  | { readonly kind: 'gifAutoLoop' }
  | { readonly kind: 'texture'; readonly path: string }
  | { readonly kind: 'uiElement'; readonly element: UiElement };

/** Something a type change adds or keeps that the user should know about. */
export type TypeGain =
  /** UI mover without an opacity track: opacity 100 % is added (the element would be invisible). */
  | { readonly kind: 'uiMoverAlpha' }
  /** GIF without progress keyframes: it plays automatically from the start. */
  | { readonly kind: 'gifAutoPlay' }
  /** A text track with this text is added. */
  | { readonly kind: 'textTrack' }
  /** A brush track (10 px) is added. */
  | { readonly kind: 'paintTrack' };

export interface TypeChangePlan {
  readonly from: LineTypeId;
  readonly losses: readonly TypeLoss[];
  readonly gains: readonly TypeGain[];
  /** UI mover target on a line with notes: refused until they are moved (count). */
  readonly blockedByNotes: number;
  /** Image / GIF target without a usable picture. */
  readonly needsTexture: boolean;
  /** UI mover target without an element. */
  readonly needsElement: boolean;
  /** Nothing would change. */
  readonly unchanged: boolean;
}

/** The current type of a line as a type-change choice. */
export function typeChoiceOf(type: LineTypeId): LineTypeChoice {
  return type === 'helper' ? 'line' : type;
}

/**
 * What changing `line` to `target` does, following the core rules of setLineType: the kind Phira draws is derived
 * from the texture and the text / paint / gif tracks, so the change removes the data of the other kinds.
 */
export function planTypeChange(
  doc: Pick<ChartDoc, 'assets' | 'keys' | 'lines'>,
  idx: Pick<DocIndexes, 'tracksOfLine' | 'keysByTrack' | 'notesByLine'>,
  line: Line,
  target: LineTypeChoice,
  opts: { readonly texture?: AssetId | null; readonly element?: UiElement | null } = {},
): TypeChangePlan {
  const from = typeOf(doc, idx, line);
  const losses: TypeLoss[] = [];
  const gains: TypeGain[] = [];
  const text = keyCountOf(idx, line.id, 'text');
  const paint = keyCountOf(idx, line.id, 'paint');
  const gif = keyCountOf(idx, line.id, 'gif');
  const texture = opts.texture === undefined ? line.texture : opts.texture;
  const textureAsset = texture === null ? undefined : doc.assets.get(texture);
  const targetIsGif = target === 'gif' && isGifAsset(textureAsset);
  if (target !== 'text' && text > 0) losses.push({ kind: 'textTrack', keys: text });
  if (target !== 'paint' && paint > 0) losses.push({ kind: 'paintTrack', keys: paint });
  if (!targetIsGif) {
    if (gif > 0) losses.push({ kind: 'gifTrack', keys: gif });
    if (line.gifAutoLoop) losses.push({ kind: 'gifAutoLoop' });
  }
  const keepsTexture = target === 'image' || target === 'gif';
  if (!keepsTexture && line.texture !== null) {
    losses.push({ kind: 'texture', path: doc.assets.get(line.texture)?.path ?? String(line.texture) });
  }
  if (target !== 'uiMover' && line.attachUI !== null) losses.push({ kind: 'uiElement', element: line.attachUI });
  if (target === 'text' && text === 0) gains.push({ kind: 'textTrack' });
  if (target === 'paint' && paint === 0) gains.push({ kind: 'paintTrack' });
  if (targetIsGif && gif === 0) gains.push({ kind: 'gifAutoPlay' });
  if (target === 'uiMover' && (idx.tracksOfLine(line.id).get('alpha') ?? []).length === 0) {
    gains.push({ kind: 'uiMoverAlpha' });
  }
  const element = opts.element === undefined ? line.attachUI : opts.element;
  const needsTexture =
    keepsTexture && (textureAsset === undefined || !isImageRef(textureAsset) || (target === 'gif' && !targetIsGif));
  const needsElement = target === 'uiMover' && element === null;
  const blockedByNotes = target === 'uiMover' ? idx.notesByLine(line.id).length : 0;
  const sameTexture = texture === line.texture;
  const sameElement = element === line.attachUI;
  const unchanged =
    typeChoiceOf(from) === target &&
    losses.length === 0 &&
    gains.length === 0 &&
    sameTexture &&
    sameElement &&
    !(target === 'gif' && !line.gifAutoLoop && gif === 0);
  return { from, losses, gains, blockedByNotes, needsTexture, needsElement, unchanged };
}

/** True for assets a line can show as a picture (textures, GIFs, illustrations, any image MIME type). */
export function isImageRef(a: AssetRef): boolean {
  if (a.role === 'texture' || a.role === 'gif' || a.role === 'illustration') return true;
  return a.mime.startsWith('image/');
}

/** Project pictures usable as a line texture (GIF lines: only GIFs Phira decodes), sorted by path. */
export function imageAssets(assets: Iterable<AssetRef> | Pick<ChartDoc, 'assets'>, gifOnly: boolean): AssetRef[] {
  const list: Iterable<AssetRef> = 'assets' in assets ? assets.assets.values() : assets;
  const out: AssetRef[] = [];
  for (const a of list) {
    if (a.missing === true || !isImageRef(a)) continue;
    if (gifOnly && !isGifAsset(a)) continue;
    out.push(a);
  }
  return out.sort((a, b) => a.path.localeCompare(b.path));
}

// ---------------------------------------------------------------------------------------------------------------
// Selection helpers
// ---------------------------------------------------------------------------------------------------------------

/** Rows between `a` and `b` (inclusive) in display order; just `b` when `a` is not listed. */
export function rowRange(rows: readonly LineRowModel[], a: LineId | null, b: LineId): LineId[] {
  const ids = rows.map((r) => r.info.id);
  const j = ids.indexOf(b);
  if (j < 0) return [];
  const i = a === null ? -1 : ids.indexOf(a);
  if (i < 0) return [b];
  return i <= j ? ids.slice(i, j + 1) : ids.slice(j, i + 1);
}

/** `ids` that exist, in export order, without duplicates. */
export function inExportOrder(doc: Pick<ChartDoc, 'lineOrder' | 'lines'>, ids: Iterable<LineId>): LineId[] {
  const set = new Set(ids);
  return doc.lineOrder.filter((id) => set.has(id) && doc.lines.has(id));
}

/** Collapsed ancestors of `line` removed from `collapsed` (so the line's row is shown); null when none were. */
export function expandTo(
  doc: Pick<ChartDoc, 'lines'>,
  collapsed: ReadonlySet<LineId>,
  line: LineId,
): Set<LineId> | null {
  const anc = ancestorsOf(doc, line).filter((a) => collapsed.has(a));
  if (anc.length === 0) return null;
  const next = new Set(collapsed);
  for (const a of anc) next.delete(a);
  return next;
}

// ---------------------------------------------------------------------------------------------------------------
// Drag and drop (tree: onto = set parent, between rows = sibling of the target; export order: reorder)
// ---------------------------------------------------------------------------------------------------------------

export type DropTarget =
  | { readonly kind: 'row'; readonly line: LineId; readonly zone: 'before' | 'after' | 'onto' }
  /** Empty space under the rows of the tree view: no parent. */
  | { readonly kind: 'root' };

export type DropPlan =
  /** `parent`: new parent (undefined = unchanged); `order`: new export order (null = unchanged). */
  | { readonly ok: true; readonly parent: LineId | null | undefined; readonly order: readonly LineId[] | null }
  | { readonly ok: false; readonly problem: ReparentProblem };

/**
 * What dropping `moving` on `target` does. Tree view: onto a row = make it the parent; before / after a row = move
 * next to it in the export order and take the target's parent (a sibling); empty space = no parent. Export-order view:
 * before / after only, the parent stays. Refused (with the reason) when it would create a cycle, and when nothing
 * would change ('unchanged').
 */
export function planDrop(
  doc: Pick<ChartDoc, 'lines' | 'lineOrder'>,
  moving: readonly LineId[],
  target: DropTarget,
  view: 'tree' | 'order',
): DropPlan {
  const ids = moving.filter((id) => doc.lines.has(id));
  if (ids.length === 0) return { ok: false, problem: { kind: 'unchanged' } };
  if (target.kind === 'root') {
    if (view !== 'tree') return { ok: false, problem: { kind: 'unchanged' } };
    const p = reparentProblem(doc, ids, null);
    return p === null ? { ok: true, parent: null, order: null } : { ok: false, problem: p };
  }
  if (!doc.lines.has(target.line)) return { ok: false, problem: { kind: 'missing', line: target.line } };
  if (ids.includes(target.line)) return { ok: false, problem: { kind: 'unchanged' } };
  if (target.zone === 'onto') {
    if (view !== 'tree') return { ok: false, problem: { kind: 'unchanged' } };
    const p = reparentProblem(doc, ids, target.line);
    return p === null ? { ok: true, parent: target.line, order: null } : { ok: false, problem: p };
  }
  const order = moveInOrder(doc.lineOrder, ids, target.line, target.zone);
  if (view === 'order') {
    return order === null ? { ok: false, problem: { kind: 'unchanged' } } : { ok: true, parent: undefined, order };
  }
  const parent = doc.lines.get(target.line)?.parent ?? null;
  const p = reparentProblem(doc, ids, parent);
  if (p !== null && p.kind !== 'unchanged') return { ok: false, problem: p };
  if (p?.kind === 'unchanged' && order === null) return { ok: false, problem: p };
  return { ok: true, parent: p === null ? parent : undefined, order };
}
